import type { LucideIcon } from 'lucide-react';
import { routeForModule } from '@shared/lib/moduleRoutes';
import type {
  CompiledMemberAppExperience,
  CompiledNavigationItem,
  CompiledPublicTile,
} from '@shared/types/domainConfig';

/**
 * Pure tab-resolution logic for the bottom tab bar, kept out of the React component so it
 * can be unit-tested. Given the permanent tabs and the current path, it decides the rendered
 * tab set and which tab is active — including the temporary "current module" tab.
 */

/** A built-in fallback tab, shown before tenant config loads. */
export interface FallbackTab {
  system_key: string;
  label: string;
  route: string;
  icon: LucideIcon;
}

/** A rendered tab: a tenant-configured nav item, or one of the built-in fallbacks. */
export type TabItem = CompiledNavigationItem | FallbackTab;

export interface ResolvedTabs {
  items: TabItem[];
  activeIndex: number;
}

/**
 * Decide the rendered tab set and active index for the current path.
 *
 * A permanent tab owns the route when its (longest) route matches. Otherwise, if a module
 * launcher tile owns the route, that module is appended on the far right as a temporary
 * "you are here" tab and becomes active; leaving the module drops it again. When nothing
 * matches, the first tab (Home) stays active. `baseItems` is the permanent set, already
 * fallback-applied and capped.
 */
export function resolveActiveTabs(
  baseItems: TabItem[],
  experience: CompiledMemberAppExperience | null,
  pathname: string,
): ResolvedTabs {
  const baseMatch = bestRouteMatch(baseItems, routeFor, pathname);
  const baseActiveIndex = baseMatch ? baseItems.indexOf(baseMatch) : -1;
  const moduleTile =
    baseActiveIndex === -1 ? bestRouteMatch(collectModuleTiles(experience), tileRoute, pathname) : null;
  const moduleTab: TabItem | null = moduleTile ? navItemFromTile(moduleTile) : null;
  const items: TabItem[] = moduleTab ? [...baseItems, moduleTab] : baseItems;
  const activeIndex = moduleTab ? items.length - 1 : Math.max(0, baseActiveIndex);
  return { items, activeIndex };
}

/** Resolve a tab item to its route (relative routes are prefixed with `/app/`). */
export function routeFor(item: TabItem): string {
  if ('route' in item && typeof item.route === 'string') {
    return item.route.startsWith('/') ? item.route : `/app/${item.route}`;
  }
  return '/app/home';
}

export function labelFor(item: TabItem): string {
  return item.label || 'Tab';
}

/** The Panic tab is rendered as an SOS badge instead of an icon. */
export function isPanicTab(item: TabItem | undefined): boolean {
  if (!item) return false;
  const systemKey = (item.system_key ?? '').trim().toLowerCase();
  const moduleKey = ('module_required' in item ? item.module_required ?? '' : '').toLowerCase();
  return systemKey === 'panic' || moduleKey === 'panic' || routeFor(item) === '/app/panic';
}

/** Home matches the app root too; other tabs match the route and its nested paths. */
export function isRouteActive(route: string, pathname: string): boolean {
  if (route === '/app/home') return pathname === '/app/home' || pathname === '/app';
  return pathname === route || pathname.startsWith(`${route}/`);
}

/**
 * The item whose route most specifically matches the current path (longest matching route
 * wins; earliest wins ties). Generic over the route accessor so it serves both permanent
 * tabs and module tiles. Returns null when nothing matches.
 */
function bestRouteMatch<T>(items: T[], getRoute: (item: T) => string | undefined, pathname: string): T | null {
  let best: T | null = null;
  let bestLength = -1;
  for (const item of items) {
    const route = getRoute(item);
    if (!route || !isRouteActive(route, pathname)) continue;
    if (route.length > bestLength) {
      bestLength = route.length;
      best = item;
    }
  }
  return best;
}

/**
 * The in-app route a module tile opens — the same resolution `runTileAction` uses: the
 * explicit `module_route`, else the `module_required` key mapped through `routeForModule`
 * (the Facilities tile, for one, ships an empty action_config and relies on the fallback).
 */
function tileRoute(tile: CompiledPublicTile): string | undefined {
  return tile.action_config.module_route ?? routeForModule(tile.module_required) ?? undefined;
}

/** Every "module" launcher tile (home grid + More categories) that resolves to a route. */
function collectModuleTiles(experience: CompiledMemberAppExperience | null): CompiledPublicTile[] {
  if (!experience) return [];
  const fromCategories = experience.tiles.more_categories?.flatMap((category) => category.tiles) ?? [];
  return [...(experience.tiles.main ?? []), ...fromCategories].filter(
    (tile) => tile.action_type === 'module' && !!tileRoute(tile),
  );
}

/** Adapt a module tile into a bottom-tab item so it renders through the same icon/route machinery. */
function navItemFromTile(tile: CompiledPublicTile): CompiledNavigationItem {
  return {
    id: tile.id,
    system_key: '',
    label: tile.label,
    icon_url: tile.image_url,
    route: tileRoute(tile) ?? '/app/home',
    module_key: '',
    placement: 'bottom_tab',
    order: 0,
    module_required: tile.module_required,
  };
}
